import { IBar, IBarComposer } from "../../models/IBars"
import { IReport } from "../../models/IReport"
import { IStorageFile } from "../../models/IStorage"
import DB_COLLECTIONS from "../../utils/database"
import { DatabaseService } from "../database/database.service"
import StorageService from "../storage/storage.service"

const normalizeRewardsText = (...values: Array<string | undefined>) => {
    for (const value of values) {
        if (typeof value === "string") {
            return value.trim();
        }
    }

    return undefined;
}

const withSyncedRewards = <T extends { rewards?: string; rewardsSummary?: string }>(bar: T): T => {
    const normalizedRewards = normalizeRewardsText(bar.rewardsSummary, bar.rewards);
    if (normalizedRewards === undefined) {
        return bar;
    }

    return {
        ...bar,
        rewards: normalizedRewards,
        rewardsSummary: normalizedRewards
    };
}

const sanitizeForFirestore = <T>(value: T): T => {
    if (Array.isArray(value)) {
        return value.map((item) => sanitizeForFirestore(item)) as unknown as T;
    }

    if (value && typeof value === 'object' && !(value instanceof Date)) {
        const sanitized: Record<string, any> = {};
        Object.entries(value as any).forEach(([key, item]) => {
            if (item !== undefined) {
                sanitized[key] = sanitizeForFirestore(item);
            }
        });
        return sanitized as T;
    }

    return value;
}

const getBars = () => {
    return DatabaseService.getCol<IBar>(DB_COLLECTIONS.bars())
        .then((bars) => bars.map((bar) => withSyncedRewards(bar)))
}

const searchBars = async (str: string) => {
    return DatabaseService.getCol<IBar>(DB_COLLECTIONS.bars(), ref => ref.where("search", 'array-contains', str.toLowerCase()))
        .then((bars) => bars.map((bar) => withSyncedRewards(bar)))
}

const getBar = (id: string) => {
    return DatabaseService.getDoc<IBar>(DB_COLLECTIONS.bar(id))
        .then((bar) => withSyncedRewards(bar))
}

const createBar = async (barC: IBarComposer) => {

    const bar: IBar = withSyncedRewards({
        ...barC,
        image: null
    });

    bar.image = await StorageService.uplaodFile(`bars/${bar.name}`, barC.image)

    const { id } = await DatabaseService.add(DB_COLLECTIONS.bars(), sanitizeForFirestore(bar));

    return { ...bar, id } as IBar

}

const updateBar = async (id: string, barC: IBarComposer) => {


    const bar: IBar = withSyncedRewards({
        ...barC,
        image: null
    });

    if (barC.image && barC.image instanceof File) {
        bar.image = await StorageService.uplaodFile(`bars/${bar.name}`, barC.image)
    } else {
        bar.image = (barC.image  as any )as IStorageFile;
    }


    await DatabaseService.update(DB_COLLECTIONS.bar(id), sanitizeForFirestore(bar));

    return { ...bar, id } as IBar

}

const deleteBar = async (id: string) => {
    await DatabaseService.remove(DB_COLLECTIONS.bar(id));
}

const getInventorySize = (id: string) => {
    return DatabaseService.getCol(DB_COLLECTIONS.inventoryItems(id))
        .then((items) => items ? items.length : 0)
}

const getPointsGiven = (id: string) => {
    return DatabaseService.getColGroup(DB_COLLECTIONS.points(), ref => ref.where("barId", '==', id))
        .then((items) => items ? items.length : 0)
}

const getRewardsGiven = (id: string) => {
    return DatabaseService.getCol(DB_COLLECTIONS.rewards(), ref => ref.where("barId", '==', id))
        .then((items) => items ? items.length : 0)
}


const getReports = (id: string): Promise<IReport> => {
    return Promise.all([
        getInventorySize(id),
        getPointsGiven(id),
        getRewardsGiven(id)
    ]).then(([
        inventorySize,
        totalPointsGiven,
        totalrewardsGiven
    ]) => ({
        inventorySize,
        totalPointsGiven,
        totalrewardsGiven
    }))
}

const BarsService = {
    createBar,
    updateBar,
    deleteBar,
    getBars,
    getBar,
    getReports,
    searchBars
}

export default BarsService
